import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserSettingsLayout from '../../../components/UserSettingsLayout';
import ContentBox from '../../../components/ContentBox';

const API_URL = process.env.REACT_APP_API_URL;

// Display name map for game identifiers — add future games here
const GAME_DISPLAY_NAMES = {
    lobbyburst: 'Lobby Burst',
};

const ENGAGEMENT_DISPLAY_NAMES = {
    like: 'Likes',
    luv:  'Luv',
};

function getGameDisplayName(identifier) {
    return GAME_DISPLAY_NAMES[identifier.toLowerCase()]
        || identifier.charAt(0).toUpperCase() + identifier.slice(1);
}

function formatNumber(n) {
    if (n === null || n === undefined) return '—';
    return Number(n).toLocaleString('en-GB');
}

function formatDate(dateString) {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

function formatXp(n) {
    if (!n) return '—';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}m`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return String(n);
}

function daysSince(dateString) {
    if (!dateString) return '—';
    const diff = Date.now() - new Date(dateString).getTime();
    return Math.floor(diff / 86_400_000).toLocaleString('en-GB');
}

function winRate(won, played) {
    if (!played) return '—';
    return `${Math.round((won / played) * 100)}%`;
}

// ── Stat tile ────────────────────────────────────────────────────────────────
function StatTile({ label, value, sub }) {
    return (
        <div
            className="bg-white border border-gray-200 p-3 flex flex-col gap-1"
            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.08)' }}
        >
            <span className="font-['Jersey_10'] text-[26px] leading-none text-gray-900 tracking-widest">
                {value}
            </span>
            <span className="font-['Jersey_10'] text-[14px] uppercase tracking-widest text-gray-500 leading-tight">
                {label}
            </span>
            {sub && (
                <span className="font-['Jersey_10'] text-[13px] text-gray-400 leading-none mt-0.5 tracking-widest">
                    {sub}
                </span>
            )}
        </div>
    );
}

// ── Category wrapper ─────────────────────────────────────────────────────────
function StatCategory({ header, tint, children }) {
    return (
        <ContentBox theme="default" tint={tint} header={header}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {children}
            </div>
        </ContentBox>
    );
}

// ── Skeleton loader ──────────────────────────────────────────────────────────
function SkeletonTile() {
    return (
        <div className="bg-gray-100 border border-gray-200 p-3 animate-pulse h-[76px]" />
    );
}

function SkeletonCategory({ header, tint, count = 6 }) {
    return (
        <ContentBox theme="default" tint={tint} header={header}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Array.from({ length: count }).map((_, i) => (
                    <SkeletonTile key={i} />
                ))}
            </div>
        </ContentBox>
    );
}

// ── Main page ────────────────────────────────────────────────────────────────
function Statistics({ userData }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData) return;
        const load = async () => {
            try {
                const res = await fetch(`${API_URL}settings/statistics`, {
                    credentials: 'include',
                });
                const data = await res.json();
                if (data.success) {
                    setStats(data.stats);
                } else {
                    toast.error('Failed to load statistics');
                }
            } catch {
                toast.error('Could not connect to server');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [userData]);

    if (!userData) return <Navigate to="/" replace />;

    if (!loading && !stats) {
        return (
            <UserSettingsLayout>
                <ContentBox theme="default" tint="lightskyblue" header="User Statistics">
                    <p className="font-['Jersey_10'] text-[16px] tracking-widest text-gray-600">
                        Could not load statistics. Please try refreshing the page.
                    </p>
                </ContentBox>
            </UserSettingsLayout>
        );
    }

    return (
        <UserSettingsLayout>
            {/* ── Membership ───────────────────────────────────────────────── */}
            {loading ? (
                <SkeletonCategory header="Membership" tint="cornflowerblue" count={5} />
            ) : (
                <StatCategory header="Membership" tint="cornflowerblue">
                    <StatTile label="Member Since"   value={formatDate(stats.memberSince)} />
                    <StatTile label="Days as Member" value={daysSince(stats.memberSince)} />
                    <StatTile label="Hotel"          value={stats.hotel || '—'} />
                    <StatTile label="Friends"        value={formatNumber(stats.social.friends)} />
                    <StatTile label="Achievements"   value={formatNumber(stats.social.achievements)} />
                </StatCategory>
            )}

            {/* ── Stamps & Cards ───────────────────────────────────────────── */}
            {loading ? (
                <SkeletonCategory header="Framps & Cards" tint="goldenrod" count={5} />
            ) : (
                <StatCategory header="Framps &amp; Cards" tint="goldenrod">
                    <StatTile label="Framps Held"         value={formatNumber(stats.stamps.currentTotal)} />
                    <StatTile label="Completed Cards"     value={formatNumber(stats.stamps.completedCards)} />
                    <StatTile label="Total Framps Earned" value={formatNumber(stats.stamps.totalEarned)} />
                    <StatTile label="Framps Spent"        value={formatNumber(stats.stamps.totalSpent)} />
                    <StatTile label="Last Framp Earned"   value={formatDate(stats.stamps.lastEarned)} />
                </StatCategory>
            )}

            {/* ── Community ────────────────────────────────────────────────── */}
            {loading ? (
                <SkeletonCategory header="Community" tint="mediumseagreen" count={8} />
            ) : (
                <StatCategory header="Community" tint="mediumseagreen">
                    <StatTile label="Profile Posts"    value={formatNumber(stats.community.profilePosts)} />
                    <StatTile label="Profile Comments" value={formatNumber(stats.community.profileComments)} />
                    <StatTile label="Article Comments" value={formatNumber(stats.community.articleComments)} />
                    <StatTile
                        label="Total Comments Left"
                        value={formatNumber(
                            stats.community.profileComments + stats.community.articleComments
                        )}
                    />
                    {Object.entries(stats.community.engagements || {}).map(([type, counts]) => {
                        const label = ENGAGEMENT_DISPLAY_NAMES[type]
                            || type.charAt(0).toUpperCase() + type.slice(1);
                        return (
                            <React.Fragment key={type}>
                                <StatTile label={`${label} Received`} value={formatNumber(counts.received)} />
                                <StatTile label={`${label} Given`}    value={formatNumber(counts.given)} />
                            </React.Fragment>
                        );
                    })}
                </StatCategory>
            )}

            {/* ── Shop & Inventory ─────────────────────────────────────────── */}
            {loading ? (
                <SkeletonCategory header="Shop & Inventory" tint="mediumpurple" count={3} />
            ) : (
                <StatCategory header="Shop &amp; Inventory" tint="mediumpurple">
                    <StatTile label="Completed Purchases" value={formatNumber(stats.shop.completedPurchases)} />
                    <StatTile label="Framp Cards Spent" value={formatNumber(stats.shop.stampsInvested)} />
                    <StatTile label="Items in Inventory"  value={formatNumber(stats.shop.inventoryItems)} />
                </StatCategory>
            )}

            {/* ── Habbo Skills ─────────────────────────────────────────────── */}
            {loading ? (
                <SkeletonCategory header="Habbo Skills" tint="teal" count={2} />
            ) : (
                <ContentBox theme="default" tint="teal" header="Habbo Skills">
                    <div className="grid grid-cols-2 gap-3">
                        <StatTile
                            label="Fishing XP"
                            value={stats.skills.fishing ? formatXp(stats.skills.fishing.experience) : '—'}
                            sub={stats.skills.fishing
                                ? `Last checked ${formatDate(stats.skills.fishing.lastChecked)}`
                                : 'Not tracked yet'}
                        />
                        <StatTile
                            label="Gardening XP"
                            value={stats.skills.gardening ? formatXp(stats.skills.gardening.experience) : '—'}
                            sub={stats.skills.gardening
                                ? `Last checked ${formatDate(stats.skills.gardening.lastChecked)}`
                                : 'Not tracked yet'}
                        />
                    </div>
                </ContentBox>
            )}

            {/* ── Games ────────────────────────────────────────────────────── */}
            {loading ? (
                <SkeletonCategory header="Games" tint="tomato" count={6} />
            ) : stats.games.length === 0 ? (
                <ContentBox theme="default" tint="tomato" header="Games">
                    <p className="font-['Jersey_10'] text-[15px] tracking-widest text-gray-500">
                        No games played yet.
                    </p>
                </ContentBox>
            ) : (
                <ContentBox theme="default" tint="tomato" header="Games">
                    <div className="flex flex-col gap-6">
                        {stats.games.map((game) => (
                            <div key={game.identifier}>
                                <p className="font-['Jersey_10'] uppercase text-[14px] tracking-widest text-gray-500 mb-2">
                                    {getGameDisplayName(game.identifier)}
                                </p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    <StatTile label="Games Played" value={formatNumber(game.played)} />
                                    <StatTile label="Games Won"    value={formatNumber(game.won)} />
                                    <StatTile label="Games Lost"   value={formatNumber(game.lost)} />
                                    <StatTile label="Win Rate"     value={winRate(game.won, game.played)} />
                                    <StatTile label="High Score"   value={formatNumber(game.highScore)} />
                                    {game.stampsFromGames > 0 && (
                                        <StatTile label="Framps Earned" value={formatNumber(game.stampsFromGames)} />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </ContentBox>
            )}
        </UserSettingsLayout>
    );
}

export default Statistics;
